import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { FiX } from "react-icons/fi";
import { isMobile } from "../utils/utils";
import { createChartDatafeed } from "./subscribes/tv_charts/src/datafeed.js";
import { getExchangeInfo } from "./subscribes/tv_charts/src/helpers.js";
import { CandleTrade, Notify, PricePick, TpSl } from "./subscribes/tv_charts/src/commands.js";
import { ORIGIN } from "../utils/configs.js";
import { buildBtcRegimeDrawings, priceBoundsFromChart } from "./btcRegimeOverlay";
import {
  buildLivePositionLanes,
  formatPnl,
  formatTradePrice,
  priceProtectLabel,
  tradePnl
} from "../trades/tradeUtils.js";

import { tradeChartMarkers, tradeCloseLabel } from "../trades/tradeChartMarkers.js";

const EMPTY_DRAWINGS = Object.freeze([]);

const whenChartDataReady = (chart, callback) => {
  let applied = false;
  const apply = () => {
    if (applied) return;
    applied = true;
    callback();
  };
  if (typeof chart.dataReady !== "function" || chart.dataReady(apply)) apply();
};

const recordedChartRange = (recordedTime, parsedInterval) => {
  const center = Number(recordedTime);
  if (!Number.isFinite(center) || center <= 0) return null;
  const seconds = parsedInterval === "5" ? 5 * 60
    : parsedInterval === "15" ? 15 * 60
      : parsedInterval === "240" ? 4 * 60 * 60
        : parsedInterval === "1D" ? 24 * 60 * 60 : 7 * 24 * 60 * 60;
  return { from: center - seconds * 40, to: center + seconds * 20 };
};

const isTypingTarget = (target) => {
  const tag = String(target?.tagName || "").toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || target?.isContentEditable;
};

const keyToPricePickField = (key) => {
  const normalized = String(key || "").toLowerCase();
  if (normalized === "s") return "stopLoss";
  if (normalized === "t") return "takeProfit";
  if (normalized === "e") return "entry";
  return null;
};

function TradingViewChart({ 
  currency, 
  base = "USDT", 
  interval, 
  timezone = "Etc/UTC", 
  pickerField, 
  drawParam, 
  historicalEventDrawings = EMPTY_DRAWINGS,
  codexEvaluationMarkers = EMPTY_DRAWINGS,
  btcRegime,
  showBtcRegime = false,
  showPreviousDayLevels = true,
  showCustomDrawings = true,
  focusLatest = false,
  focusTime = null,
  assessmentLevels = null,
  showAssessmentLevels = true,
  assessmentMode = false,
  enableTradeCommands = false,
  theme = "dark",
  isSpot = false,
  onChartReady,
  onChartFailed,
  onCodexEvaluationSelect,
}) {
  const containerRef = useRef(null);
  const widgetRef = useRef(null);
  const chartRef = useRef(null);
  const chartDatafeedRef = useRef(null);
  const chartTimersRef = useRef(new Set());
  const [isLibraryLoaded, setIsLibraryLoaded] = useState(false);
  const [positionLegend, setPositionLegend] = useState([]);
  const [selectedPositionIndex, setSelectedPositionIndex] = useState(0);
  const [positionLegendCollapsed, setPositionLegendCollapsed] = useState(true);
  const [tradeLegend, setTradeLegend] = useState([]);
  const [selectedTradeIndex, setSelectedTradeIndex] = useState(0);
  const [tradeLegendCollapsed, setTradeLegendCollapsed] = useState(true);
  const [selectedTradeDetails, setSelectedTradeDetails] = useState(null);
  const isInitializedRef = useRef(false);
  const pricePickKeyRef = useRef(null);
  const lastChartCursorRef = useRef({ time: null, price: null });
  const chartEventCleanupRef = useRef(null);
  const tradeDrawingEventCleanupRef = useRef(null);
  const tradeDrawingEntitiesRef = useRef(new Map());
  const tradeLabelCleanupRef = useRef(null);
  const tradeDrawingRequestRef = useRef(0);
  const btcRegimeRef = useRef(btcRegime);
  const historicalEventDrawingsRef = useRef(historicalEventDrawings);
  const historicalEventEntitiesRef = useRef(new Map());
  const codexEvaluationMarkersRef = useRef(codexEvaluationMarkers);
  const codexEvaluationEntitiesRef = useRef(new Map());
  const codexEvaluationByEntityRef = useRef(new Map());
  const onCodexEvaluationSelectRef = useRef(onCodexEvaluationSelect);
  const crosshairDrawTimeoutRef = useRef(null);
  const chartEvaluationEntitiesRef = useRef([]);
  const btcRegimeEntitiesRef = useRef([]);
  const customDrawingEntitiesRef = useRef(new Set());
  const customDrawingsVisibleRef = useRef(showCustomDrawings);
  const previousDayLevelsVisibleRef = useRef(showPreviousDayLevels);
  const previousDayLevelEntitiesRef = useRef([]);
  const previousDayRequestRef = useRef(0);
  const chartEvaluationLevelsRef = useRef(null);
  const assessmentLevelsRef = useRef(assessmentLevels);
  const showAssessmentLevelsRef = useRef(showAssessmentLevels);
  const assessmentLevelEntitiesRef = useRef(new Map());
  const priceDiffEntityRef = useRef(null);
  const priceDiffParamRef = useRef(null);

  const scheduleChartTask = useCallback((chart, callback, delay) => {
    const timer = setTimeout(() => {
      chartTimersRef.current.delete(timer);
      if (chartRef.current === chart) callback();
    }, delay);
    chartTimersRef.current.add(timer);
  }, []);

  const registerCustomDrawing = useCallback((entityId) => {
    if (entityId != null) customDrawingEntitiesRef.current.add(entityId);
    return entityId;
  }, []);

  const removeCustomDrawings = useCallback((chart) => {
    if (!chart) return;
    previousDayRequestRef.current += 1;
    tradeDrawingRequestRef.current += 1;
    tradeLabelCleanupRef.current?.();
    tradeLabelCleanupRef.current = null;
    previousDayLevelEntitiesRef.current = [];
    customDrawingEntitiesRef.current.forEach((entityId) => {
      try { chart.removeEntity(entityId); } catch (_) {}
    });
    customDrawingEntitiesRef.current.clear();
    historicalEventEntitiesRef.current = new Map();
    tradeDrawingEntitiesRef.current = new Map();
    chartEvaluationEntitiesRef.current = [];
    btcRegimeEntitiesRef.current = [];
  }, []);

  useEffect(() => {
    btcRegimeRef.current = btcRegime;
  }, [btcRegime]);

  useEffect(() => {
    historicalEventDrawingsRef.current = historicalEventDrawings;
  }, [historicalEventDrawings]);

  useEffect(() => {
    codexEvaluationMarkersRef.current = codexEvaluationMarkers;
  }, [codexEvaluationMarkers]);

  useEffect(() => {
    onCodexEvaluationSelectRef.current = onCodexEvaluationSelect;
  }, [onCodexEvaluationSelect]);

  useEffect(() => {
    customDrawingsVisibleRef.current = showCustomDrawings;
  }, [showCustomDrawings]);

  useEffect(() => {
    assessmentLevelsRef.current = assessmentLevels;
  }, [assessmentLevels]);

  useEffect(() => {
    showAssessmentLevelsRef.current = showAssessmentLevels;
  }, [showAssessmentLevels]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (isTypingTarget(event.target)) return;
      const field = keyToPricePickField(event.key);
      if (field) pricePickKeyRef.current = field;
    };
    const onKeyUp = (event) => {
      if (!keyToPricePickField(event.key)) return;
      pricePickKeyRef.current = null;
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const onChartContainerClick = () => {
      const field = pricePickKeyRef.current;
      const cursor = lastChartCursorRef.current;
      if (!field || cursor.price == null) return;
      const symbol = `${currency}${base}`;
      window.dispatchEvent(new CustomEvent("trading_panel_price_pick", {
        detail: {
          field,
          currency,
          symbol,
          price: Number(cursor.price),
          time: cursor.time
        }
      }));
    };
    container.addEventListener("click", onChartContainerClick);
    return () => container.removeEventListener("click", onChartContainerClick);
  }, [base, currency]);

  const dispatchTradingPanelPricePick = useCallback((field, currency, base) => {
    const cursor = lastChartCursorRef.current;
    if (!field || cursor.price == null) return;
    const symbol = `${currency}${base}`;
    window.dispatchEvent(new CustomEvent("trading_panel_price_pick", {
      detail: {
        field,
        currency,
        symbol,
        price: Number(cursor.price),
        time: cursor.time
      }
    }));
  }, []);

  const attachTradingViewFrameEvents = useCallback((widget, currency, base) => {
    if (chartEventCleanupRef.current) {
      chartEventCleanupRef.current();
      chartEventCleanupRef.current = null;
    }

    const iframe = widget?._iFrame || containerRef.current?.querySelector("iframe");
    const frameDocument = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!frameDocument) return;

    const onFrameKeyDown = (event) => {
      if (isTypingTarget(event.target)) return;
      const field = keyToPricePickField(event.key);
      if (field) {
        pricePickKeyRef.current = field;
        event.preventDefault();
        event.stopPropagation();
      }
    };
    const onFrameKeyUp = (event) => {
      if (!keyToPricePickField(event.key)) return;
      pricePickKeyRef.current = null;
      event.preventDefault();
      event.stopPropagation();
    };
    const onFramePointerDown = () => {
      dispatchTradingPanelPricePick(pricePickKeyRef.current, currency, base);
    };

    frameDocument.addEventListener("keydown", onFrameKeyDown, true);
    frameDocument.addEventListener("keyup", onFrameKeyUp, true);
    frameDocument.addEventListener("mousedown", onFramePointerDown, true);
    frameDocument.addEventListener("touchstart", onFramePointerDown, true);

    chartEventCleanupRef.current = () => {
      frameDocument.removeEventListener("keydown", onFrameKeyDown, true);
      frameDocument.removeEventListener("keyup", onFrameKeyUp, true);
      frameDocument.removeEventListener("mousedown", onFramePointerDown, true);
      frameDocument.removeEventListener("touchstart", onFramePointerDown, true);
    };
  }, [dispatchTradingPanelPricePick]);

  const attachTradeDrawingEvents = useCallback((widget) => {
    if (tradeDrawingEventCleanupRef.current) {
      tradeDrawingEventCleanupRef.current();
      tradeDrawingEventCleanupRef.current = null;
    }

    const onDrawingEvent = (sourceId, eventType) => {
      if (widgetRef.current !== widget || !isInitializedRef.current) return;
      if (eventType !== "click") return;
      const evaluation = codexEvaluationByEntityRef.current.get(String(sourceId));
      if (evaluation) {
        onCodexEvaluationSelectRef.current?.(evaluation);
        return;
      }
      const trade = tradeDrawingEntitiesRef.current.get(String(sourceId));
      if (trade) {
        if (Number.isInteger(trade.legendIndex)) setSelectedTradeIndex(trade.legendIndex);
        setTradeLegendCollapsed(false);
        setSelectedTradeDetails(trade);
      }
    };

    widget.subscribe("drawing_event", onDrawingEvent);
    tradeDrawingEventCleanupRef.current = () => {
      try {
        widget.unsubscribe("drawing_event", onDrawingEvent);
      } catch (err) {
        console.error("Failed to detach trade drawing events:", err);
      }
    };
  }, []);

  const reconcileCodexEvaluationMarkers = useCallback((chart, markers) => {
    if (!chart) return;
    const entities = codexEvaluationEntitiesRef.current;
    const nextKeys = new Set();

    (Array.isArray(markers) ? markers : []).forEach((marker, index) => {
      const key = marker.key || `codex-evaluation-${index}`;
      const signature = `${marker.time}-${marker.price}-${marker.label}-${marker.color}`;
      nextKeys.add(key);
      const existing = entities.get(key);
      if (existing?.signature === signature) {
        codexEvaluationByEntityRef.current.set(String(existing.entityId), marker.evaluation);
        return;
      }
      if (existing?.entityId != null) {
        try { chart.removeEntity(existing.entityId); } catch (_) {}
        codexEvaluationByEntityRef.current.delete(String(existing.entityId));
      }
      try {
        const entityId = chart.createShape(
          { time: marker.time, price: marker.price },
          {
            shape: "text",
            text: marker.label,
            disableSelection: false,
            disableSave: true,
            lock: true,
            overrides: {
              color: marker.color,
              textcolor: marker.color,
              fontsize: 12,
              bold: true,
            },
            zOrder: "top",
          }
        );
        if (entityId != null) {
          entities.set(key, { entityId, signature });
          codexEvaluationByEntityRef.current.set(String(entityId), marker.evaluation);
        }
      } catch (error) {
        console.error("Failed to draw Codex evaluation marker:", error);
        entities.delete(key);
      }
    });

    [...entities.entries()].forEach(([key, entity]) => {
      if (nextKeys.has(key)) return;
      try { chart.removeEntity(entity.entityId); } catch (_) {}
      codexEvaluationByEntityRef.current.delete(String(entity.entityId));
      entities.delete(key);
    });
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!isInitializedRef.current || !chart) return;
    reconcileCodexEvaluationMarkers(chart, codexEvaluationMarkers);
  }, [codexEvaluationMarkers, reconcileCodexEvaluationMarkers]);

  useEffect(() => {
    if (selectedPositionIndex >= positionLegend.length) {
      setSelectedPositionIndex(0);
    }
  }, [selectedPositionIndex, positionLegend.length]);

  useEffect(() => {
    if (selectedTradeIndex >= tradeLegend.length) {
      setSelectedTradeIndex(0);
    }
  }, [selectedTradeIndex, tradeLegend.length]);

  // Load TradingView library
  useEffect(() => {
    if (window.TradingView) {
      setIsLibraryLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = process.env.PUBLIC_URL
      ? `${process.env.PUBLIC_URL}/tv_charts/charting_library/charting_library.standalone.js`
      : "/tv_charts/charting_library/charting_library.standalone.js";
    script.async = true;
    script.onload = () => {
      if (window.TradingView) {
        setIsLibraryLoaded(true);
      }
    };
    script.onerror = () => {
      console.error("Failed to load TradingView library");
      if (onChartFailed) onChartFailed();
    };
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [onChartFailed]);

  // Helper functions
  const removePreviousDayLevels = useCallback((chart) => {
    previousDayRequestRef.current += 1;
    previousDayLevelEntitiesRef.current.forEach(entityId => {
      try { chart?.removeEntity(entityId); } catch (_) {}
      customDrawingEntitiesRef.current.delete(entityId);
    });
    previousDayLevelEntitiesRef.current = [];
  }, []);

  const drawPreviousDayLevels = useCallback(async (chart, interval, currency, base) => {
    removePreviousDayLevels(chart);
    if (!previousDayLevelsVisibleRef.current || !customDrawingsVisibleRef.current || interval === "1D" || interval === "1W") return;
    const request = previousDayRequestRef.current;

    try {
      const pair = `${currency}${base}`;
      const query = `symbol=${pair}&interval=1d&limit=2`;
      const response = await fetch(`https://${isSpot ? "api" : "fapi"}.binance.com/${isSpot ? "api/v3" : "fapi/v1"}/klines?${query}`);
      if (!response.ok) return;
      const data = await response.json();
      if (request !== previousDayRequestRef.current || !previousDayLevelsVisibleRef.current
          || !customDrawingsVisibleRef.current || !Array.isArray(data) || data.length < 2) return;

      const candle = data[0];
      const pdHigh = Number(candle[2]);
      const pdLow = Number(candle[3]);
      const pdCloseTimeSec = candle[6] / 1000;
      if (![pdHigh, pdLow, pdCloseTimeSec].every(value => Number.isFinite(value) && value > 0)) return;
      const registerPreviousDayLevel = entityId => {
        if (entityId != null) previousDayLevelEntitiesRef.current.push(registerCustomDrawing(entityId));
      };

      registerPreviousDayLevel(chart.createShape(
        { time: candle[0] / 1000, price: pdHigh },
        {
          shape: "horizontal_ray",
          text: "PDH",
          disableSelection: false,
          disableSave: true,
          lock: false,
          overrides: {
            linecolor: "#FF0000",
            linestyle: 2,
            textcolor: "#FF0000",
            text: "PDH"
          },
          zOrder: "top"
        }
      ));

      registerPreviousDayLevel(chart.createShape(
        { time: candle[0] / 1000, price: pdLow },
        {
          shape: "horizontal_ray",
          text: "PDL",
          disableSelection: false,
          disableSave: true,
          lock: false,
          overrides: {
            linecolor: "#00FF00",
            linestyle: 2,
            textcolor: "#00FF00",
            text: "PDL"
          },
          zOrder: "top"
        }
      ));

      registerPreviousDayLevel(chart.createShape(
        { time: pdCloseTimeSec, price: pdHigh * 1.003 },
        {
          shape: "arrow_down",
          text: "PDC",
          disableSelection: false,
          disableSave: true,
          lock: false,
          overrides: {
            fillcolor: "yellow",
            textcolor: "yellow",
            text: "PDC"
          },
          zOrder: "top"
        }
      ));
    } catch (err) {
      console.error("Failed to draw PDH/PDL/PDC:", err);
    }
  }, [isSpot, registerCustomDrawing, removePreviousDayLevels]);

  useEffect(() => {
    previousDayLevelsVisibleRef.current = showPreviousDayLevels;
    const chart = chartRef.current;
    if (!showPreviousDayLevels) {
      removePreviousDayLevels(chart);
    } else if (isInitializedRef.current && chart && !assessmentMode) {
      drawPreviousDayLevels(chart, interval, currency, base);
    }
  }, [showPreviousDayLevels, assessmentMode, interval, currency, base, drawPreviousDayLevels, removePreviousDayLevels]);

  const base62Decode = useCallback((str) => {
    const BASE62_ALPHABET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
    // eslint-disable-next-line no-undef
    let value = BigInt(0);
    for (const c of str) {
      // eslint-disable-next-line no-undef
      value = value * 62n + BigInt(BASE62_ALPHABET.indexOf(c));
    }

    const bytes = [];
    // eslint-disable-next-line no-undef
    while (value > 0) {
      // eslint-disable-next-line no-undef
      bytes.unshift(Number(value % 256n));
      // eslint-disable-next-line no-undef
      value /= 256n;
    }

    return new TextDecoder().decode(new Uint8Array(bytes));
  }, []);

  const decodeDrawData = useCallback((base62String) => {
    try {
      const json = base62Decode(base62String);
      return JSON.parse(json);
    } catch (err) {
      console.error("Failed to decode draw param:", err);
      return [];
    }
  }, [base62Decode]);

  const supportedRecordedDrawings = useCallback((items) => {
    const shapes = new Set(["rectangle", "horizontal_ray", "arrow_down", "arrow_up", "text", "trend_line", "vertical_line"]);
    return (Array.isArray(items) ? items : []).flatMap((item) => {
      const shape = String(item?.options?.shape || "");
      if (!shapes.has(shape) || !Array.isArray(item.point) || item.point.length < 1 || item.point.length > 8) return [];
      const points = item.point.map((point) => ({ time: Number(point?.time), price: Number(point?.price) }));
      if (points.some((point) => !Number.isFinite(point.time) || !Number.isFinite(point.price) || point.price <= 0)) return [];
      return [{ point: points, options: { ...item.options, shape } }];
    });
  }, []);

  const drawObjects = useCallback((chart, items) => {
    const entityIds = [];
    if (!items || !chart || items.length === 0) return entityIds;
    items.forEach(obj => {
      if (obj.point && obj.options) {
        try {
          let entityId = null;
          if (obj.point.length > 1) {
            entityId = chart.createMultipointShape(obj.point, obj.options);
          } else if (obj.point.length === 1) {
            entityId = chart.createShape(obj.point[0], obj.options);
          }
          if (entityId != null) entityIds.push(registerCustomDrawing(entityId));
        } catch (err) {
          console.error("Failed to draw chart object:", err);
        }
      }
    });
    return entityIds;
  }, [registerCustomDrawing]);

  const reconcileAssessmentLevels = useCallback((chart, levels, visible, anchorTime) => {
    if (!chart) return;
    const entities = assessmentLevelEntitiesRef.current;
    const colors = { entry: "#60a5fa", stopLoss: "#fb7185", takeProfit1: "#4ade80", takeProfit2: "#22c55e" };
    const labels = { entry: "AI Entry", stopLoss: "AI Stop", takeProfit1: "AI TP1", takeProfit2: "AI TP2" };
    const nextKeys = new Set();
    if (visible) Object.entries(levels || {}).forEach(([key, rawPrice]) => {
      const price = Number(rawPrice);
      if (!colors[key] || !Number.isFinite(price) || price <= 0) return;
      nextKeys.add(key);
      const signature = `${price}-${Number(anchorTime) || 0}`;
      const existing = entities.get(key);
      if (existing?.signature === signature) return;
      if (existing?.entityId != null) {
        try { chart.removeEntity(existing.entityId); } catch (_) {}
      }
      try {
        const entityId = chart.createShape({ time: Number(anchorTime) || Math.floor(Date.now() / 1000), price }, {
          shape: "horizontal_ray",
          text: labels[key],
          disableSelection: false,
          disableSave: true,
          lock: true,
          overrides: { linecolor: colors[key], textcolor: colors[key], linestyle: 2, linewidth: 1, text: labels[key] },
          zOrder: "top",
        });
        if (entityId != null) entities.set(key, { entityId, signature });
      } catch (error) {
        console.error("Failed to draw recorded AI assessment level:", error);
      }
    });
    [...entities.entries()].forEach(([key, entity]) => {
      if (nextKeys.has(key)) return;
      try { chart.removeEntity(entity.entityId); } catch (_) {}
      entities.delete(key);
    });
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!isInitializedRef.current || !chart) return;
    reconcileAssessmentLevels(chart, assessmentLevels, showAssessmentLevels, focusTime);
  }, [assessmentLevels, focusTime, reconcileAssessmentLevels, showAssessmentLevels]);

  const drawPriceDifferenceLabel = useCallback((chart, param) => {
    if (!chart || !param?.price) return;
    const bars = chart.getSeries?.()?.data?.().bars?.();
    if (!bars?._items?.length) return;
    const lastPrice = Number(bars._items[bars._items.length - 1]?.value?.[4]);
    if (!Number.isFinite(lastPrice) || lastPrice === 0) return;
    const text = `    ${(((Number(param.price) - lastPrice) / lastPrice) * 100).toFixed(2)}%`;
    if (priceDiffEntityRef.current != null) {
      try { chart.removeEntity(priceDiffEntityRef.current); } catch (_) {}
    }
    try {
      priceDiffEntityRef.current = chart.createMultipointShape(
        [{ time: param.time, price: Number(param.price) }],
        {
          shape: "text",
          text,
          disableSelection: true,
          disableSave: true,
          color: "#FFD700",
          overrides: { color: theme === "light" ? "#ffae00" : "#FFD700" },
          textAlign: "center",
          fontsize: 16,
          bold: true,
          zOrder: "top"
        }
      );
    } catch (error) {
      console.error("Failed to draw price difference label:", error);
    }
  }, [theme]);

  useEffect(() => {
    const onDrawLevels = (event) => {
      const detail = event.detail || {};
      if (detail.symbol && detail.symbol !== `${currency}${base}`) return;
      const chart = chartRef.current;
      if (!chart) return;
      chartEvaluationLevelsRef.current = detail.levels || {};
      if (!customDrawingsVisibleRef.current) return;
      chartEvaluationEntitiesRef.current.forEach((id) => {
        try { chart.removeEntity(id); } catch (_) {}
      });
      chartEvaluationEntitiesRef.current = [];

      const colors = { entry: "#60a5fa", stopLoss: "#fb7185", takeProfit1: "#4ade80", takeProfit2: "#22c55e" };
      const labels = { entry: "AI Entry", stopLoss: "AI Stop", takeProfit1: "AI TP1", takeProfit2: "AI TP2" };
      const time = Number(lastChartCursorRef.current?.time) || Math.floor(Date.now() / 1000);
      Object.entries(detail.levels || {}).forEach(([key, rawPrice]) => {
        const price = Number(rawPrice);
        if (!Number.isFinite(price) || price <= 0 || !colors[key]) return;
        try {
          const id = chart.createShape({ time, price }, {
            shape: "horizontal_ray",
            text: labels[key],
            disableSelection: false,
            disableSave: true,
            lock: false,
            overrides: { linecolor: colors[key], textcolor: colors[key], linestyle: 2, linewidth: 1, text: labels[key] },
            zOrder: "top",
          });
          if (id != null) chartEvaluationEntitiesRef.current.push(registerCustomDrawing(id));
        } catch (error) {
          console.error("Failed to draw AI evaluation level:", error);
        }
      });
    };
    window.addEventListener("chart_evaluation_draw", onDrawLevels);
    return () => window.removeEventListener("chart_evaluation_draw", onDrawLevels);
  }, [base, currency, registerCustomDrawing]);

  const reconcileHistoricalEventDrawings = useCallback((chart, drawings) => {
    if (!chart) return;
    const entities = historicalEventEntitiesRef.current;
    const nextKeys = new Set();

    drawings.forEach((drawing, index) => {
      const key = drawing.overlayKey || `historical-event-${index}`;
      const signature = drawing.overlaySignature || JSON.stringify(drawing);
      nextKeys.add(key);

      const existing = entities.get(key);
      if (existing?.signature === signature) return;
      if (existing?.entityId != null) {
        try { chart.removeEntity(existing.entityId); } catch {}
        customDrawingEntitiesRef.current.delete(existing.entityId);
      }

      const [entityId] = drawObjects(chart, [drawing]);
      if (entityId != null) {
        entities.set(key, { entityId, signature });
      } else {
        entities.delete(key);
      }
    });

    [...entities.entries()].forEach(([key, entity]) => {
      if (nextKeys.has(key)) return;
      try { chart.removeEntity(entity.entityId); } catch {}
      customDrawingEntitiesRef.current.delete(entity.entityId);
      entities.delete(key);
    });
  }, [drawObjects]);

  useEffect(() => {
    const widget = widgetRef.current;
    const chart = chartRef.current;
    if (!isInitializedRef.current || !chart) return;
    if (!showCustomDrawings) removeCustomDrawings(chart);
    else whenChartDataReady(chart, () => {
      if (widgetRef.current === widget && customDrawingsVisibleRef.current) {
        reconcileHistoricalEventDrawings(chart, historicalEventDrawingsRef.current);
      }
    });
  }, [historicalEventDrawings, reconcileHistoricalEventDrawings, removeCustomDrawings, showCustomDrawings]);

  const drawBtcRegimeOverlay = useCallback((chart, parsedInterval, attempt = 0) => {
    if (!chart || !customDrawingsVisibleRef.current || chartRef.current !== chart) return;
    const priceBounds = priceBoundsFromChart(chart);
    if (!priceBounds && attempt < 5) {
      scheduleChartTask(chart, () => drawBtcRegimeOverlay(chart, parsedInterval, attempt + 1), 250);
      return;
    }
    btcRegimeEntitiesRef.current.forEach((entityId) => {
      try { chart.removeEntity(entityId); } catch (_) {}
      customDrawingEntitiesRef.current.delete(entityId);
    });
    const drawings = buildBtcRegimeDrawings(btcRegimeRef.current, priceBounds, parsedInterval);
    btcRegimeEntitiesRef.current = drawObjects(chart, drawings);
  }, [drawObjects, scheduleChartTask]);

  const focusLatestRange = useCallback((chart, parsedInterval) => {
    if (!chart || typeof chart.setVisibleRange !== "function") return;
    const secondsByInterval = {
      "5": 5 * 60,
      "15": 15 * 60,
      "240": 4 * 60 * 60,
      "1D": 24 * 60 * 60,
      "1W": 7 * 24 * 60 * 60
    };
    const candleSeconds = secondsByInterval[parsedInterval] || 15 * 60;
    const to = Math.floor(Date.now() / 1000) + candleSeconds * 8;
    const from = to - candleSeconds * 120;
    try {
      chart.setVisibleRange({ from, to });
    } catch (err) {
      console.error("Failed to focus latest chart range:", err);
    }
  }, []);

  const focusRecordedRange = useCallback((chart, recordedTime, parsedInterval) => {
    const range = recordedChartRange(recordedTime, parsedInterval);
    if (!chart || typeof chart.setVisibleRange !== "function" || !range) return;
    try {
      chart.setVisibleRange(range);
    } catch (error) {
      console.error("Failed to focus recorded assessment time:", error);
    }
  }, []);

  const focusRecordedRangeWhenReady = useCallback((chart, recordedTime, parsedInterval) => {
    if (!chart) return;
    let applied = false;
    const apply = () => {
      if (chartRef.current !== chart) return;
      if (applied) return;
      applied = true;
      focusRecordedRange(chart, recordedTime, parsedInterval);
    };
    if (typeof chart.dataReady === "function") {
      const ready = chart.dataReady(apply);
      if (ready) apply();
      return;
    }
    apply();
  }, [focusRecordedRange]);

  const createSupertrendStudy = useCallback((chart) => {
    try {
      chart.createStudy("Supertrend", false, true, [10, 3]);
    } catch (err) {
      try {
        chart.createStudy("SuperTrend", false, true, [10, 3]);
      } catch (fallbackErr) {
        console.error("Failed to create Supertrend study:", fallbackErr);
      }
    }
  }, []);

  const fetchTradesForSymbol = useCallback(async (currency, base) => {
    if (!currency || !base) return [];
    try {
      const symbol = `${currency}${base}`.toUpperCase();
      const response = await fetch(`${ORIGIN}/insights/account/trades?symbol=${encodeURIComponent(symbol)}`,
        { credentials: "same-origin", cache: "no-store" });
      if (!response.ok) return [];
      const payload = await response.json();
      return payload.trades || [];
    } catch (err) {
      console.error("Failed to load trade drawings:", err);
      return [];
    }
  }, []);

  const fetchPositionJournalForSymbol = useCallback(async (currency, base) => {
    if (!currency || !base) return { lanes: [], orders: [], fills: [], executions: [] };
    try {
      const symbol = `${currency}${base}`.toUpperCase();
      const response = await fetch(`${ORIGIN}/insights/account/positions?symbol=${encodeURIComponent(symbol)}`,
        { credentials: "same-origin", cache: "no-store" });
      if (!response.ok) return { lanes: [], orders: [], fills: [], executions: [] };
      const payload = await response.json();
      const current = payload.status === "CURRENT";
      return {
        lanes: current ? payload.lanes || [] : [],
        orders: current ? payload.orders || [] : [],
        fills: payload.fills || [],
        executions: payload.executions || []
      };
    } catch (err) {
      console.error("Failed to load position journal drawings:", err);
      return { lanes: [], orders: [], fills: [], executions: [] };
    }
  }, []);

  const refreshPositionLegend = useCallback(async () => {
    if (!currency || !base) return;
    const symbol = `${currency}${base}`.toUpperCase();
    const positionJournal = await fetchPositionJournalForSymbol(currency, base);
    setPositionLegend(buildLivePositionLanes(positionJournal, symbol));
  }, [base, currency, fetchPositionJournalForSymbol]);

  useEffect(() => {
    const onRefresh = (event) => {
      const requestedSymbol = String(event?.detail?.symbol || "").toUpperCase();
      const currentSymbol = `${currency || ""}${base || ""}`.toUpperCase();
      if (requestedSymbol && requestedSymbol !== currentSymbol) return;
      refreshPositionLegend();
    };
    window.addEventListener("position_journal_refresh", onRefresh);
    return () => window.removeEventListener("position_journal_refresh", onRefresh);
  }, [base, currency, refreshPositionLegend]);

  useEffect(() => {
    if (!currency || !base) return undefined;
    const intervalId = setInterval(refreshPositionLegend, 20000);
    return () => clearInterval(intervalId);
  }, [base, currency, refreshPositionLegend]);

  const toChartTime = useCallback((value) => {
    if (!value) return null;
    const ts = Number(value);
    if (!Number.isFinite(ts)) return null;
    return ts < 1000000000000 ? ts : Math.floor(ts / 1000);
  }, []);

  const toMillis = useCallback((value) => {
    if (!value) return null;
    const ts = Number(value);
    if (!Number.isFinite(ts) || ts <= 0) return null;
    return ts < 1000000000000 ? ts * 1000 : ts;
  }, []);

  const tradeStartMillis = useCallback((trade) => {
    return toMillis(trade?.submittedAt || trade?.entryFilledAt);
  }, [toMillis]);

  const tradeEndMillis = useCallback((trade) => {
    const closedAt = toMillis(trade?.closedAt);
    if (closedAt != null) return closedAt;
    return ["SUBMITTED", "WAITING_ENTRY", "OPEN", "PARTIALLY_CLOSED"].includes(trade?.status)
      ? null
      : toMillis(trade?.entryFilledAt);
  }, [toMillis]);

  const positionItemMillis = useCallback((item) => {
    return toMillis(item?.updatedAt || item?.time || item?.createdAt);
  }, [toMillis]);

  const isWithinTradeWindow = useCallback((item, trade) => {
    const itemTime = positionItemMillis(item);
    if (itemTime == null) return true;
    const start = tradeStartMillis(trade);
    if (start != null && itemTime < start) return false;
    const end = tradeEndMillis(trade);
    if (end != null && itemTime > end + 60000) return false;
    return true;
  }, [positionItemMillis, tradeEndMillis, tradeStartMillis]);

  const samePositionSide = useCallback((itemSide, tradeSide, positionAmt) => {
    if (!tradeSide) return true;
    if (!itemSide || itemSide === tradeSide) return true;
    if (itemSide === "BOTH") {
      const amt = Number(positionAmt);
      if (!Number.isFinite(amt)) return true;
      return tradeSide === "LONG" ? amt > 0 : amt < 0;
    }
    return false;
  }, []);

  const itemMatchesTrade = useCallback((item, trade) => {
    return String(item?.symbol || "").toUpperCase() === String(trade?.symbol || "").toUpperCase()
      && samePositionSide(String(item?.positionSide || "").toUpperCase(), trade?.side, item?.positionAmt)
      && isWithinTradeWindow(item, trade);
  }, [isWithinTradeWindow, samePositionSide]);

  const isStopOrder = useCallback((order) => {
    const type = String(order?.type || "").toUpperCase();
    return type.includes("STOP") || order?.stopPrice != null;
  }, []);

  const createPriceLine = useCallback((chart, time, price, color, style = 2, endTime = null) => {
    if (!customDrawingsVisibleRef.current || !time || price == null) return;
    const options = {
      shape: endTime && endTime > time ? "trend_line" : "horizontal_ray",
      disableSelection: false,
      disableSave: true,
      lock: false,
      overrides: {
        linecolor: color,
        textcolor: color,
        linestyle: style,
        linewidth: 1
      },
      zOrder: "top"
    };
    if (endTime && endTime > time) {
      return registerCustomDrawing(chart.createMultipointShape(
        [{ time, price }, { time: endTime, price }],
        options
      ));
    }
    return registerCustomDrawing(chart.createShape({ time, price }, options));
  }, [registerCustomDrawing]);

  const createTradePointMarker = useCallback((chart, time, price, side, action) => {
    if (!customDrawingsVisibleRef.current || !time || price == null || !side || !action) return;
    const color = side === "LONG" ? "rgba(76, 175, 80, 0.78)" : "rgba(233, 30, 99, 0.78)";
    const shape = action === "OPEN" ? "arrow_right" : "arrow_left";

    return registerCustomDrawing(chart.createShape(
      { time, price },
      {
        shape,
        disableSelection: false,
        disableSave: true,
        lock: false,
        overrides: {
          arrowColor: color,
          color,
          fillcolor: color,
          linecolor: color,
          textcolor: color,
          showLabel: false,
          text: ""
        },
        zOrder: "top"
      }
    ));
  }, [registerCustomDrawing]);

  const createFillText = useCallback((chart, time, price, text, side) => {
    if (!customDrawingsVisibleRef.current || !time || price == null || !text) return;
    const color = side === "LONG" ? "#dcfce7" : "#ffe4e6";
    try {
      return registerCustomDrawing(chart.createShape(
        { time, price },
        {
          shape: "text",
          text,
          disableSelection: false,
          disableSave: true,
          lock: false,
          overrides: {
            color,
            textcolor: color,
            fontsize: 13,
            bold: true,
            italic: false,
            fixedSize: true,
            fillBackground: true,
            backgroundColor: "#161b25",
            backgroundTransparency: 0,
            drawBorder: true,
            borderColor: side === "LONG" ? "#4caf50" : "#e91e63",
            text
          },
          zOrder: "top"
        }
      ));
    } catch (err) {
      console.error("Failed to draw fill label:", err);
    }
  }, [registerCustomDrawing]);

  const createSetupLine = useCallback((chart, time, price, color) => {
    if (!customDrawingsVisibleRef.current || !time || price == null) return null;
    try {
      return registerCustomDrawing(chart.createShape(
        { time, price },
        {
          shape: "vertical_line",
          text: "",
          disableSelection: false,
          disableSave: true,
          lock: true,
          overrides: {
            linecolor: color,
            linestyle: 2,
            linewidth: 1,
            text: "",
            showLabel: false
          },
          zOrder: "top"
        }
      ));
    } catch (err) {
      console.error("Failed to draw trade setup line:", err);
      return null;
    }
  }, [registerCustomDrawing]);

  const getTradeAmountText = useCallback((trade) => {
    if (trade?.value && trade?.leverage) {
      return `${trade.value} USDT x${trade.leverage}`;
    }
    const entry = trade?.actualEntry || trade?.plannedEntry;
    if (entry != null && trade?.initialQuantity != null) {
      const notional = Math.abs(Number(entry) * Number(trade.initialQuantity));
      if (Number.isFinite(notional) && notional > 0) {
        return `${notional.toFixed(2)} USDT`;
      }
    }
    return "";
  }, []);

  const getStopLossPercent = useCallback((trade) => {
    const entry = trade?.actualEntry || trade?.plannedEntry;
    if (trade?.stopLoss == null || entry == null) return null;
    const pct = Math.abs((Number(trade.stopLoss) - Number(entry)) / Number(entry) * 100);
    return Number.isFinite(pct) ? pct : null;
  }, []);

  const getTakeProfitText = useCallback((trade) => {
    const entry = trade?.actualEntry || trade?.plannedEntry;
    if (entry == null) return "TP HOLD";
    const entryPrice = Number(entry);
    const sideMultiplier = trade?.side === "SHORT" ? -1 : 1;
    const formatTp = (price) => {
      if (price == null) return null;
      const pct = (Number(price) - entryPrice) / entryPrice * 100 * sideMultiplier;
      return Number.isFinite(pct)
        ? `${formatTradePrice(price)} (${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%)`
        : formatTradePrice(price);
    };
    const tp1 = formatTp(trade?.tp1);
    const tp2 = formatTp(trade?.tp2);
    if (!tp1 && !tp2) return "TP HOLD";
    if (tp1 && !tp2 && !trade?.tp2Hold) return `TP ${tp1}`;
    return `TP1 ${tp1 || "HOLD"} / TP2 ${trade?.tp2Hold ? "HOLD" : (tp2 || "HOLD")}`;
  }, []);

  const fillSide = useCallback((fill, trades) => {
    const matchedTrade = trades.find((trade) => itemMatchesTrade(fill, trade));
    if (matchedTrade?.side) return matchedTrade.side;
    const positionSide = String(fill?.positionSide || "").toUpperCase();
    if (positionSide === "LONG" || positionSide === "SHORT") return positionSide;
    return fill?.side === "SELL" ? "SHORT" : "LONG";
  }, [itemMatchesTrade]);

  const drawTradeObjects = useCallback(async (chart, currency, base) => {
    const widget = widgetRef.current;
    if (!widget || !chart || chartRef.current !== chart) return;
    const request = ++tradeDrawingRequestRef.current;
    const [trades, positionJournal] = await Promise.all([
      fetchTradesForSymbol(currency, base),
      fetchPositionJournalForSymbol(currency, base)
    ]);
    if (widgetRef.current !== widget || request !== tradeDrawingRequestRef.current) return;
    setPositionLegend(buildLivePositionLanes(positionJournal, `${currency}${base}`));
    tradeDrawingEntitiesRef.current = new Map();
    setSelectedTradeDetails(null);

    const legendTrades = trades.map((trade, index) => ({
      ...trade,
      displaySymbol: trade.symbol || `${currency}${base}`,
      legendKey: `${trade.id || trade.processName || trade.submittedAt || "trade"}-${index}`,
      legendIndex: index
    }));
    setTradeLegend(legendTrades);
    setSelectedTradeIndex(0);
    setTradeLegendCollapsed(true);

    whenChartDataReady(chart, () => {
      if (widgetRef.current !== widget || request !== tradeDrawingRequestRef.current || !customDrawingsVisibleRef.current) return;
      const datafeed = chartDatafeedRef.current;
      if (!datafeed) return;
      const markerItems = tradeChartMarkers(positionJournal);
      const hasExecution = (trade, action) => markerItems.some((item) =>
        itemMatchesTrade(item, trade)
        && (action === "OPEN" ? ["OPEN", "ADD"] : ["CLOSE", "REDUCE"]).includes(String(item.classification || "").toUpperCase())
      );
      tradeLabelCleanupRef.current?.();
      const pendingLabels = [];
      const pendingPoints = [];
      const priceLines = [];
      const ownedEntities = new Set();
      const rememberEntity = (id, trade) => {
        if (id == null) return;
        ownedEntities.add(id);
        if (trade) tradeDrawingEntitiesRef.current.set(String(id), trade);
      };
      const queuePoint = (time, draw, trade) => {
        if (time) pendingPoints.push({ time, draw, trade });
      };
      const queuePriceLine = (time, price, color, endTime = null) => {
        if (time && price != null) priceLines.push({ time, price, color, endTime });
      };
      const placeDrawings = () => {
        if (widgetRef.current !== widget || request !== tradeDrawingRequestRef.current || !customDrawingsVisibleRef.current) return;
        // Only use candle opens delivered to this widget. A date outside its
        // history must remain pending instead of being snapped to a recent bar.
        for (let i = pendingPoints.length - 1; i >= 0; i--) {
          const point = pendingPoints[i];
          const candle = datafeed.candleAt(point.time);
          if (!candle) continue;
          const id = point.draw(candle.time / 1000);
          if (id != null) {
            rememberEntity(id, point.trade);
            pendingPoints.splice(i, 1);
          }
        }
        const range = datafeed.loadedRange();
        if (range) priceLines.forEach(line => {
          // Keep levels of an older active trade visible, but never extend a
          // closed trade into today's history or invent its entry timestamp.
          if (line.endTime && line.endTime < range.from) return;
          const from = datafeed.candleAt(Math.max(line.time, range.from));
          const to = line.endTime ? datafeed.candleAt(Math.min(line.endTime, range.to)) : null;
          if (!from || (line.endTime && !to)) return;
          const start = from.time / 1000;
          const end = to ? to.time / 1000 : null;
          if (end != null && end <= start) return;
          const signature = `${start}:${end}`;
          if (line.signature === signature) return;
          if (line.id != null) {
            chart.removeEntity(line.id);
            ownedEntities.delete(line.id);
            customDrawingEntitiesRef.current.delete(line.id);
          }
          line.id = createPriceLine(chart, start, line.price, line.color, 2, end);
          if (line.id != null) {
            line.signature = signature;
            rememberEntity(line.id);
          }
        });
        for (let i = pendingLabels.length - 1; i >= 0; i--) {
          const label = pendingLabels[i];
          const candle = datafeed.candleAt(label.time);
          const edge = Number(label.side === "LONG" ? candle?.high : candle?.low);
          if (!(edge > 0)) continue;
          const price = edge * (label.side === "LONG" ? 1.005 : 0.995);
          const id = createFillText(chart, candle.time / 1000, price, label.text, label.side);
          if (id != null) {
            rememberEntity(id, label.trade);
            pendingLabels.splice(i, 1);
          }
        }
      };
      const dataLoaded = chart.onDataLoaded();
      dataLoaded.subscribe(null, placeDrawings);
      tradeLabelCleanupRef.current = () => {
        dataLoaded.unsubscribe(null, placeDrawings);
        pendingLabels.length = 0;
        pendingPoints.length = 0;
        priceLines.length = 0;
        ownedEntities.forEach(id => {
          try { chart.removeEntity(id); } catch (_) {}
          customDrawingEntitiesRef.current.delete(id);
          tradeDrawingEntitiesRef.current.delete(String(id));
        });
        ownedEntities.clear();
      };

      legendTrades.forEach((trade) => {
        const entry = trade.actualEntry || trade.plannedEntry;
        const entryTime = toChartTime(trade.entryFilledAt || trade.submittedAt);
        const setupTime = toChartTime(trade.submittedAt) || entryTime;
        const sideColor = trade.side === "LONG" ? "#4caf50" : "#e91e63";
        const entryColor = "#a7f3d0";
        const stopColor = "#fb923c";
        const tpColor = "#7dd3fc";
        const lineEndTime = toChartTime(trade.closedAt);
        const exchangeOrders = (positionJournal.orders || []).filter((order) => itemMatchesTrade(order, trade));
        const hasExchangeStop = exchangeOrders.some(isStopOrder);

        queuePoint(setupTime, time => createSetupLine(chart, time, entry, sideColor), trade);
        queuePriceLine(trade.actualEntry ? entryTime : setupTime, entry, entryColor, lineEndTime);
        // The durable trade outlives the bounded exchange journal. Use only recorded
        // executions as fallbacks; planned entries and intermediate fills are not fills.
        if (Number(trade.actualEntry) > 0 && toChartTime(trade.entryFilledAt) && !hasExecution(trade, "OPEN")) {
          queuePoint(entryTime, time => createTradePointMarker(chart, time, Number(trade.actualEntry), trade.side, "OPEN"), trade);
        }
        if (Number(trade.actualClose) > 0 && lineEndTime && !hasExecution(trade, "CLOSE")) {
          queuePoint(lineEndTime, time => createTradePointMarker(chart, time, Number(trade.actualClose), trade.side, "CLOSE"), trade);
          const pnl = tradePnl(trade);
          pendingLabels.push({ time: lineEndTime, side: trade.side, trade,
            text: tradeCloseLabel({ price: trade.actualClose, netPnl: pnl }, trade.side, trade) });
        }

        if (!hasExchangeStop && trade.stopLoss != null && entry != null) {
          queuePriceLine(setupTime, trade.stopLoss, stopColor, lineEndTime);
        }

        if (trade.tp1 != null) {
          queuePriceLine(setupTime, trade.tp1, tpColor, lineEndTime);
        }
        if (trade.tp2 != null) {
          queuePriceLine(setupTime, trade.tp2, tpColor, lineEndTime);
        }
      });

      (positionJournal.orders || []).forEach((order) => {
        const time = toChartTime(order.createdAt || order.updatedAt);
        const price = Number(order.stopPrice || order.price);
        if (!time || !Number.isFinite(price) || price <= 0) return;
        const color = isStopOrder(order) ? "#fb923c" : "#7dd3fc";
        queuePriceLine(time, price, color);
      });

      const seenFillKeys = new Set();
      markerItems.forEach((fill) => {
        const time = toChartTime(fill.markerTime);
        const price = Number(fill.markerPrice);
        if (!time || !Number.isFinite(price) || price <= 0) return;
        const key = fill.markerKey || `${fill.orderId}-${fill.markerTime}-${fill.markerPrice}-${fill.quantity}`;
        if (seenFillKeys.has(key)) return;
        seenFillKeys.add(key);
        const side = fillSide(fill, trades);
        const action = ["REDUCE", "CLOSE"].includes(fill.classification) ? "CLOSE" : "OPEN";
        queuePoint(time, candleTime => createTradePointMarker(chart, candleTime, price, side, action));
        if (action === "CLOSE") {
          const trade = trades.find(item => itemMatchesTrade(fill, item));
          const text = tradeCloseLabel(fill, side, trade);
          if (text) pendingLabels.push({ time, text, side, trade });
        }
      });
      placeDrawings();
    });
  }, [
    createFillText,
    createPriceLine,
    createSetupLine,
    createTradePointMarker,
    fetchPositionJournalForSymbol,
    fetchTradesForSymbol,
    fillSide,
    isStopOrder,
    itemMatchesTrade,
    toChartTime
  ]);

  const redrawCustomDrawings = useCallback((chart) => {
    if (!chart || !customDrawingsVisibleRef.current) return;
    const parsedInterval = interval === "5M" ? "5"
      : interval === "15M" ? "15"
        : interval === "4H" ? "240"
          : interval === "1D" ? "1D" : "1W";

    if (drawParam) {
      const decoded = decodeDrawData(drawParam);
      drawObjects(chart, assessmentMode ? supportedRecordedDrawings(decoded) : decoded);
    }
    if (historicalEventDrawingsRef.current.length > 0) {
      reconcileHistoricalEventDrawings(chart, historicalEventDrawingsRef.current);
    }
    if (!assessmentMode) drawTradeObjects(chart, currency, base);
    if (!assessmentMode && showBtcRegime) drawBtcRegimeOverlay(chart, parsedInterval);
    if (!assessmentMode && parsedInterval !== "1D" && parsedInterval !== "1W") {
      drawPreviousDayLevels(chart, parsedInterval, currency, base);
    }
    if (priceDiffParamRef.current) drawPriceDifferenceLabel(chart, priceDiffParamRef.current);
    if (chartEvaluationLevelsRef.current) {
      window.dispatchEvent(new CustomEvent("chart_evaluation_draw", {
        detail: { symbol: `${currency}${base}`, levels: chartEvaluationLevelsRef.current }
      }));
    }
  }, [assessmentMode, base, currency, decodeDrawData, drawBtcRegimeOverlay, drawObjects, drawPreviousDayLevels, drawParam, drawPriceDifferenceLabel, drawTradeObjects, interval, reconcileHistoricalEventDrawings, showBtcRegime, supportedRecordedDrawings]);

  useEffect(() => {
    const onRestore = (event) => {
      if (event.detail?.symbol && event.detail.symbol !== `${currency}${base}`) return;
      const chart = chartRef.current;
      redrawCustomDrawings(chart);
    };
    window.addEventListener("chart_custom_drawings_restore", onRestore);
    return () => window.removeEventListener("chart_custom_drawings_restore", onRestore);
  }, [base, currency, redrawCustomDrawings]);

  const appliedCustomDrawingsVisibilityRef = useRef(showCustomDrawings);
  useEffect(() => {
    const chart = chartRef.current;
    if (!isInitializedRef.current || !chart) return;
    if (appliedCustomDrawingsVisibilityRef.current === showCustomDrawings) return;
    appliedCustomDrawingsVisibilityRef.current = showCustomDrawings;
    if (!showCustomDrawings) {
      removeCustomDrawings(chart);
      return;
    }
    redrawCustomDrawings(chart);
  }, [redrawCustomDrawings, removeCustomDrawings, showCustomDrawings]);

  // Initialize chart when library is loaded
  useLayoutEffect(() => {
    if (!isLibraryLoaded || !containerRef.current || !currency) {
      return;
    }

    let disposed = false;
    let ownedWidget = null;
    let crosshairSubscription = null;
    let onCrosshairMove = null;

    // Reset initialization flag
    isInitializedRef.current = false;

    const initializeChart = async () => {
      try {
        // Set global isSpot function for datafeed
        window.__isSpot = () => isSpot;

        const mobile = isMobile();
        const parsedInterval = interval === "5M" ? "5" : 
                              interval === "15M" ? "15" : 
                              interval === "4H" ? "240" :
                              interval === "1D" ? "1D" : "1W";

        const crossHairMoveParams = {
          time: null,
          price: null
        };

        // Optional overlays must not gate chart initialization.
        await getExchangeInfo();
        if (disposed) return;
        const symbol = `${currency}${base}`;

        const uiCommands = assessmentMode && !enableTradeCommands ? [] : !pickerField
          ? [
              new CandleTrade(crossHairMoveParams, currency, "LONG", parsedInterval, "📈"),
              new CandleTrade(crossHairMoveParams, currency, "SHORT", parsedInterval, "📉"),
              new TpSl(crossHairMoveParams, currency, parsedInterval, "💰🛡️"),
              new Notify(crossHairMoveParams, currency, "🔔")
            ]
          : [
              new PricePick(crossHairMoveParams, currency, pickerField, "🎯")
            ];

        const chartDatafeed = createChartDatafeed(symbol, parsedInterval);
        chartDatafeedRef.current = chartDatafeed;
        const widget = new window.TradingView.widget({
          symbol: `Binance:${currency}/${base}`,
          interval: parsedInterval,
          timeframe: assessmentMode ? recordedChartRange(focusTime, parsedInterval) || undefined : undefined,
          fullscreen: false,
          container: containerRef.current,
          datafeed: chartDatafeed,
          library_path: process.env.PUBLIC_URL
            ? `${process.env.PUBLIC_URL}/tv_charts/charting_library/`
            : "/tv_charts/charting_library/",
          theme: theme,
          overrides: {
            "paneProperties.background": theme === "dark" ? "#000000" : "#ffffff",
            "paneProperties.backgroundType": "solid",
            volumePaneSize: "10%"
          },
          timezone: timezone,
          header_widget_buttons_mode: "compact",
          disabled_features: [
            mobile ? "" : "header_widget",
            "symbol_search_hot_key",
            "source_selection_markers",
            "legend_widget",
            "timeframes_toolbar",
            mobile ? "left_toolbar" : "",
            "header_interval_dialog_button",
            "header_chart_type",
            "header_indicators",
            "header_compare",
            "header_undo_redo",
            "header_screenshot",
            "header_fullscreen_button",
            "header_settings",
            "timeframes_toolbar"
          ],
          enabled_features: [
            "volume_force_overlay",
            "create_volume_indicator_by_default",
            mobile ? "hide_right_toolbar" : "",
            mobile ? "hide_price_scale_if_all_sources_hidden" : ""
          ],
          studies_overrides: {
            "moving average.source": "close",
            "moving average.length": "99",
            "moving average.plot.color": "#66CC66",
            "moving average exponential.source": "close",
            "moving average exponential.length": "200",
            "moving average exponential.plot.color": "#FFA500",
            "bollinger bands.length": 20,
            "bollinger bands.median.visibility": 0
          },
          context_menu: {
            items_processor: (defaultItems, actionFactory) => {
              const buttons = [];
              const menuCursor = { ...crossHairMoveParams };
              uiCommands.forEach(command => {
                buttons.push(actionFactory.createAction({
                  label: command.getCommandDef(),
                  icon: command.icon,
                  onExecute: () => {
                    command.sendToInsightsApp(menuCursor);
                  }
                }));
              });
              return buttons;
            }
          },
          allow_symbol_change: false,
          width: "100%",
          height: mobile ? 400 : 700
        });
        // Own the widget before its asynchronous readiness callback so cleanup
        // can remove it even while the iframe is still loading.
        widgetRef.current = widget;
        ownedWidget = widget;

        widget.onChartReady(() => {
          if (disposed || widgetRef.current !== widget) return;
          try {
            widget.applyOverrides({
              "paneProperties.background": theme === "dark" ? "#1E2026" : "#ffffff",
              "paneProperties.backgroundType": "solid"
            });
            
            const chart = widget.chart();
            chartRef.current = chart;
            if (chart) {
              chart.createStudy("Moving Average", false, true);
              chart.createStudy("Bollinger Bands", false, true);
              createSupertrendStudy(chart);
              
              if (!pickerField) {
                chart.createStudy("MACD", false, true);
                chart.createStudy("Volume", true, true);
                chart.createStudy("Relative Strength Index", false, true);
              }

              if (customDrawingsVisibleRef.current && drawParam) {
                const decoded = decodeDrawData(drawParam);
                const itemsToDraw = assessmentMode ? supportedRecordedDrawings(decoded) : decoded;
                whenChartDataReady(chart, () => {
                  if (widgetRef.current !== widget || !customDrawingsVisibleRef.current) return;
                  drawObjects(chart, itemsToDraw);
                });
              }

              if (customDrawingsVisibleRef.current && historicalEventDrawingsRef.current.length > 0) {
                whenChartDataReady(chart, () => {
                  if (widgetRef.current !== widget || !customDrawingsVisibleRef.current) return;
                  reconcileHistoricalEventDrawings(chart, historicalEventDrawingsRef.current);
                });
              }

              if (!assessmentMode) scheduleChartTask(chart, () => {
                if (widgetRef.current !== widget) return;
                drawTradeObjects(chart, currency, base).finally(() => {
                  if (widgetRef.current !== widget) return;
                  if (focusLatest) {
                    scheduleChartTask(chart, () => {
                      if (widgetRef.current !== widget) return;
                      focusLatestRange(chart, parsedInterval);
                      if (customDrawingsVisibleRef.current && showBtcRegime) {
                        scheduleChartTask(chart, () => drawBtcRegimeOverlay(chart, parsedInterval), 300);
                      }
                    }, 150);
                  } else if (customDrawingsVisibleRef.current && showBtcRegime) {
                    scheduleChartTask(chart, () => drawBtcRegimeOverlay(chart, parsedInterval), 500);
                  }
                });
              }, 700);

              if (!assessmentMode && customDrawingsVisibleRef.current && parsedInterval !== "1D" && parsedInterval !== "1W") {
                drawPreviousDayLevels(chart, parsedInterval, currency, base);
              }
              reconcileCodexEvaluationMarkers(chart, codexEvaluationMarkersRef.current);
              reconcileAssessmentLevels(chart, assessmentLevelsRef.current, showAssessmentLevelsRef.current, focusTime);
              if (focusTime) focusRecordedRangeWhenReady(chart, focusTime, parsedInterval);
            }

            crosshairSubscription = chart.crossHairMoved();
            onCrosshairMove = (param) => {
              if (disposed || chartRef.current !== chart) return;
              if (!param || param.price == null) return;
    
              crossHairMoveParams.time = param.time;
              crossHairMoveParams.price = param.price;
              lastChartCursorRef.current = {
                time: param.time,
                price: param.price
              };
              priceDiffParamRef.current = param;
    
              if (crosshairDrawTimeoutRef.current) clearTimeout(crosshairDrawTimeoutRef.current);
              crosshairDrawTimeoutRef.current = setTimeout(() => {
                if (disposed || chartRef.current !== chart) return;
                drawPriceDifferenceLabel(chart, param);
              }, 20);
            };
            crosshairSubscription.subscribe(null, onCrosshairMove);

            widget.onContextMenu((unixtime, price) => {
              if (disposed || widgetRef.current !== widget) return [];
              const clickedCandle = window.__insightsCandleAt?.(symbol, parsedInterval, unixtime) || null;
              const clickedContext = { time: unixtime, price, selectedCandle: clickedCandle };
              const items = [];
              uiCommands.forEach(command => {
                items.push(command.getContextMenuItem(clickedContext));
              });
              return items;
            });

            if (mobile) {
              widget.headerReady().then(() => {
                if (disposed || widgetRef.current !== widget) return;
                uiCommands.forEach(command => {
                  command.createWidgetButton(widget);
                });
              });
            }

            attachTradingViewFrameEvents(widget, currency, base);
            attachTradeDrawingEvents(widget);
            isInitializedRef.current = true;
            
            window.dispatchEvent(new CustomEvent('chart_ready', { detail: {} }));
            if (onChartReady) onChartReady();
          } catch (error) {
            console.error("Error in onChartReady:", error);
            isInitializedRef.current = false;
          }
        });

      } catch (error) {
        if (disposed) return;
        console.error("Failed to initialize chart:", error);
        isInitializedRef.current = false;
        window.dispatchEvent(new CustomEvent('chart_failed', { detail: {} }));
        if (onChartFailed) onChartFailed();
      }
    };

    initializeChart();

    return () => {
      disposed = true;
      isInitializedRef.current = false;
      chartRef.current = null;
      chartDatafeedRef.current = null;
      widgetRef.current = null;
      chartTimersRef.current.forEach(clearTimeout);
      chartTimersRef.current.clear();
      previousDayRequestRef.current += 1;
      previousDayLevelEntitiesRef.current = [];
      if (chartEventCleanupRef.current) {
        chartEventCleanupRef.current();
        chartEventCleanupRef.current = null;
      }
      if (tradeDrawingEventCleanupRef.current) {
        tradeDrawingEventCleanupRef.current();
        tradeDrawingEventCleanupRef.current = null;
      }
      if (crosshairDrawTimeoutRef.current != null) {
        clearTimeout(crosshairDrawTimeoutRef.current);
        crosshairDrawTimeoutRef.current = null;
      }
      if (crosshairSubscription && onCrosshairMove) {
        crosshairSubscription.unsubscribe(null, onCrosshairMove);
      }
      tradeDrawingRequestRef.current += 1;
      tradeLabelCleanupRef.current?.();
      tradeLabelCleanupRef.current = null;
      // Layout cleanup runs while the iframe is still attached. Detach listeners
      // before removing this effect's widget, then invalidate every delayed task.
      if (ownedWidget) {
        try {
          ownedWidget.remove();
        } catch (e) {
          console.error("Error removing widget:", e);
        }
      }
      historicalEventEntitiesRef.current = new Map();
      tradeDrawingEntitiesRef.current = new Map();
      codexEvaluationEntitiesRef.current = new Map();
      codexEvaluationByEntityRef.current = new Map();
      assessmentLevelEntitiesRef.current = new Map();
      customDrawingEntitiesRef.current = new Set();
      chartEvaluationEntitiesRef.current = [];
      btcRegimeEntitiesRef.current = [];
      isInitializedRef.current = false;
    };
  }, [isLibraryLoaded, currency, base, interval, timezone, pickerField, drawParam, showBtcRegime, focusLatest, focusTime, assessmentMode, enableTradeCommands, theme, isSpot, createSupertrendStudy, drawBtcRegimeOverlay, drawTradeObjects, focusLatestRange, focusRecordedRangeWhenReady, attachTradingViewFrameEvents, attachTradeDrawingEvents, reconcileHistoricalEventDrawings, reconcileCodexEvaluationMarkers, reconcileAssessmentLevels, drawPreviousDayLevels, drawObjects, drawPriceDifferenceLabel, supportedRecordedDrawings, scheduleChartTask]);

  const selectedPosition = positionLegend[Math.min(selectedPositionIndex, positionLegend.length - 1)];
  const chartIsMobile = isMobile();
  const visiblePositionChips = positionLegend.slice(0, 4);

  const positionSummaryText = (position) => {
    if (!position?.lane) return "Awaiting fill";
    const quantity = Math.abs(Number(position.lane.positionAmt));
    const notional = Math.abs(Number(position.lane.notional));
    const parts = [];
    if (Number.isFinite(quantity)) parts.push(`${quantity.toFixed(quantity >= 10 ? 3 : 5).replace(/\.?0+$/, "")} qty`);
    if (Number.isFinite(notional)) parts.push(`${notional.toFixed(2)} USDT`);
    if (position.lane.leverage) parts.push(`x${position.lane.leverage}`);
    return parts.join(" / ");
  };

  const positionOrderLabel = (order) => {
    const type = String(order?.type || "ORDER").toUpperCase();
    if (type.includes("STOP")) return "SL";
    if (type.includes("TAKE_PROFIT")) return "TP";
    if (order?.reduceOnly || order?.closePosition) return "REDUCE";
    return "OPEN";
  };

  const positionOrderPrice = (order) => formatTradePrice(order?.stopPrice || order?.price);

  const selectedTradeSideColor = selectedTradeDetails?.side === "SHORT" ? "#f43f5e" : "#4ade80";
  const selectedTradeSlPercent = selectedTradeDetails ? getStopLossPercent(selectedTradeDetails) : null;
  const selectedTradePnl = selectedTradeDetails ? tradePnl(selectedTradeDetails) : null;
  const tradeLegendTop = chartIsMobile
    ? 50
    : positionLegend.length > 0
      ? (positionLegendCollapsed ? 86 : 302)
      : 54;
  const selectedTradeOverlayTop = tradeLegendTop + 36;

  return (
    <div style={{ position: "relative", width: "100%", height: chartIsMobile ? "400px" : "700px" }}>
      <div 
        ref={containerRef} 
        style={{ width: "100%", height: "100%" }}
      />
      {selectedTradeDetails && (
        <div
          style={{
            position: "absolute",
            top: selectedTradeOverlayTop,
            right: chartIsMobile ? 8 : 12,
            left: chartIsMobile ? 8 : "auto",
            zIndex: 8,
            width: chartIsMobile ? "auto" : 310,
            maxWidth: "calc(100% - 16px)",
            pointerEvents: "auto",
            color: theme === "dark" ? "#e5e7eb" : "#111827",
            fontSize: 12,
            lineHeight: 1.3,
            background: theme === "dark" ? "rgba(17, 24, 39, 0.94)" : "rgba(255, 255, 255, 0.96)",
            border: theme === "dark" ? "1px solid rgba(148, 163, 184, 0.32)" : "1px solid rgba(15, 23, 42, 0.2)",
            borderLeft: `3px solid ${selectedTradeSideColor}`,
            boxShadow: "0 10px 28px rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(8px)",
            borderRadius: 6,
            padding: "9px 10px"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
            <strong style={{ color: selectedTradeSideColor, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {selectedTradeDetails.side} {selectedTradeDetails.displaySymbol || `${currency}${base}`}
            </strong>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <span style={{ color: theme === "dark" ? "#cbd5e1" : "#475569", fontSize: 10, fontWeight: 700 }}>
                {String(selectedTradeDetails.status || "UNKNOWN").replaceAll("_", " ")}
              </span>
              <button
                type="button"
                onClick={() => {
                  setSelectedTradeDetails(null);
                  setTradeLegendCollapsed(true);
                }}
                title="Close trade details"
                aria-label="Close trade details"
                style={{
                  width: 24,
                  height: 24,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid rgba(148, 163, 184, 0.28)",
                  borderRadius: 4,
                  background: "transparent",
                  color: theme === "dark" ? "#cbd5e1" : "#475569",
                  cursor: "pointer",
                  padding: 0
                }}
              >
                <FiX size={16} aria-hidden="true" />
              </button>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "78px minmax(0, 1fr)", gap: "5px 9px" }}>
            <span style={{ color: theme === "dark" ? "#94a3b8" : "#64748b" }}>Amount</span>
            <strong>{getTradeAmountText(selectedTradeDetails) || "Unknown"}</strong>
            <span style={{ color: theme === "dark" ? "#94a3b8" : "#64748b" }}>Planned</span>
            <span>{formatTradePrice(selectedTradeDetails.plannedEntry)}</span>
            {selectedTradeDetails.actualEntry != null && (
              <>
                <span style={{ color: theme === "dark" ? "#94a3b8" : "#64748b" }}>Filled</span>
                <span>{formatTradePrice(selectedTradeDetails.actualEntry)}</span>
              </>
            )}
            <span style={{ color: theme === "dark" ? "#94a3b8" : "#64748b" }}>Protection</span>
            <span>{priceProtectLabel(selectedTradeDetails).replace(/^Protect\s*/i, "") || "OFF"}</span>
            <span style={{ color: theme === "dark" ? "#94a3b8" : "#64748b" }}>Stop loss</span>
            <span>
              {selectedTradeDetails.stopLoss == null
                ? "HOLD"
                : `${formatTradePrice(selectedTradeDetails.stopLoss)}${selectedTradeSlPercent == null ? "" : ` (-${selectedTradeSlPercent.toFixed(1)}%)`}`}
            </span>
            <span style={{ color: theme === "dark" ? "#94a3b8" : "#64748b" }}>Take profit</span>
            <span>{getTakeProfitText(selectedTradeDetails).replace(/^TP\s*/i, "")}</span>
            {selectedTradePnl != null && (
              <>
                <span style={{ color: theme === "dark" ? "#94a3b8" : "#64748b" }}>PnL</span>
                <strong style={{ color: Number(selectedTradePnl) >= 0 ? "#4ade80" : "#f87171" }}>
                  {formatPnl(selectedTradePnl)}
                </strong>
              </>
            )}
          </div>
        </div>
      )}
      {tradeLegend.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: tradeLegendTop,
            right: chartIsMobile ? 8 : 12,
            zIndex: 9,
            width: tradeLegendCollapsed ? "auto" : (chartIsMobile ? "auto" : 238),
            maxWidth: "calc(100% - 16px)",
            pointerEvents: "auto",
            color: theme === "dark" ? "#e5e7eb" : "#1f2937",
            fontSize: 11,
            lineHeight: 1.2,
            background: theme === "dark" ? "rgba(17, 24, 39, 0.88)" : "rgba(255, 255, 255, 0.92)",
            border: theme === "dark" ? "1px solid rgba(148, 163, 184, 0.28)" : "1px solid rgba(15, 23, 42, 0.18)",
            boxShadow: "0 8px 20px rgba(0, 0, 0, 0.22)",
            backdropFilter: "blur(6px)",
            borderRadius: 6,
            padding: "4px 5px 4px 7px"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 5, minWidth: 0 }}>
            <strong style={{ whiteSpace: "nowrap", color: theme === "dark" ? "#cbd5e1" : "#334155" }}>
              Trades ({tradeLegend.length})
            </strong>
            {!tradeLegendCollapsed && (
              <div
                style={{
                  display: "flex",
                  gap: 4,
                  overflowX: "auto",
                  flex: 1,
                  justifyContent: "flex-start",
                  minWidth: 0,
                  scrollbarWidth: "none"
                }}
              >
                {tradeLegend.map((trade, index) => {
                  const sideColor = trade.side === "SHORT" ? "#f43f5e" : "#4ade80";
                  const isSelected = index === selectedTradeIndex;
                  return (
                    <button
                      key={trade.legendKey}
                      type="button"
                      onClick={() => {
                        setSelectedTradeIndex(index);
                        setSelectedTradeDetails(trade);
                      }}
                      title={`${trade.side} ${trade.displaySymbol}`}
                      style={{
                        minWidth: 28,
                        height: 20,
                        borderRadius: 4,
                        border: isSelected ? `1px solid ${sideColor}` : "1px solid rgba(148, 163, 184, 0.25)",
                        background: isSelected ? "rgba(148, 163, 184, 0.18)" : "rgba(15, 23, 42, 0.25)",
                        color: sideColor,
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "0 5px",
                        cursor: "pointer"
                      }}
                    >
                      {trade.side === "SHORT" ? "S" : "L"}{index + 1}
                    </button>
                  );
                })}
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                if (tradeLegendCollapsed) {
                  const nextTrade = tradeLegend[Math.min(selectedTradeIndex, tradeLegend.length - 1)];
                  setTradeLegendCollapsed(false);
                  setSelectedTradeDetails(nextTrade || null);
                } else {
                  setTradeLegendCollapsed(true);
                  setSelectedTradeDetails(null);
                }
              }}
              title={tradeLegendCollapsed ? "Show trade details" : "Hide trade details"}
              aria-label={tradeLegendCollapsed ? "Show trade details" : "Hide trade details"}
              style={{
                width: 20,
                height: 20,
                borderRadius: 4,
                border: "1px solid rgba(148, 163, 184, 0.28)",
                background: "rgba(15, 23, 42, 0.28)",
                color: theme === "dark" ? "#e5e7eb" : "#334155",
                lineHeight: "16px",
                cursor: "pointer",
                padding: 0
              }}
            >
              {tradeLegendCollapsed ? "+" : "-"}
            </button>
          </div>
        </div>
      )}
      {positionLegend.length > 0 && !chartIsMobile && (
        <div
          style={{
            position: "absolute",
            top: 54,
            right: 12,
            zIndex: 5,
            width: positionLegendCollapsed ? "auto" : "320px",
            maxWidth: "calc(100% - 24px)",
            maxHeight: positionLegendCollapsed ? "34px" : "240px",
            overflow: "hidden",
            pointerEvents: "auto",
            color: "#e5e7eb",
            fontSize: positionLegendCollapsed ? 11 : 12,
            lineHeight: 1.25,
            background: theme === "dark" ? "rgba(17, 24, 39, 0.82)" : "rgba(255, 255, 255, 0.88)",
            border: theme === "dark" ? "1px solid rgba(148, 163, 184, 0.28)" : "1px solid rgba(15, 23, 42, 0.18)",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.24)",
            backdropFilter: "blur(6px)",
            borderRadius: 6,
            padding: positionLegendCollapsed ? "4px 5px 4px 7px" : "7px 9px"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: positionLegendCollapsed ? 5 : 8, minWidth: 0 }}>
            <strong style={{ fontSize: positionLegendCollapsed ? 11 : 12, color: "#cbd5e1", whiteSpace: "nowrap" }}>
              Positions ({positionLegend.length})
            </strong>
            <div style={{ display: "flex", gap: 4, overflow: "hidden", flex: positionLegendCollapsed ? "0 1 auto" : 1, justifyContent: "flex-end", minWidth: 0 }}>
              {visiblePositionChips.map((position, index) => {
                const pnl = Number(position.lane?.unrealizedPnl);
                const sideColor = position.positionSide === "LONG" ? "#4ade80" : "#f87171";
                const isSelected = index === selectedPositionIndex;
                const pnlText = position.pending
                  ? "P"
                  : Number.isFinite(pnl) ? `${pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}` : position.positionSide.charAt(0);
                return (
                  <button
                    key={position.key}
                    type="button"
                    onClick={() => {
                      setSelectedPositionIndex(index);
                      setPositionLegendCollapsed(false);
                    }}
                    title={`${position.pending ? "Pending " : ""}${position.positionSide} ${position.symbol}`}
                    style={{
                      minWidth: positionLegendCollapsed ? 28 : 34,
                      height: positionLegendCollapsed ? 20 : 22,
                      borderRadius: 4,
                      border: isSelected ? `1px solid ${sideColor}` : "1px solid rgba(148, 163, 184, 0.25)",
                      background: isSelected ? "rgba(148, 163, 184, 0.18)" : "rgba(15, 23, 42, 0.34)",
                      color: position.pending ? "#fbbf24" : Number.isFinite(pnl) && pnl < 0 ? "#f87171" : "#4ade80",
                      fontSize: positionLegendCollapsed ? 10 : 11,
                      fontWeight: 700,
                      padding: positionLegendCollapsed ? "0 5px" : "0 6px",
                      cursor: "pointer"
                    }}
                  >
                    {position.positionSide.charAt(0)} {pnlText}
                  </button>
                );
              })}
              {positionLegend.length > visiblePositionChips.length && (
                <span style={{ color: "#9ca3af", fontSize: 11, alignSelf: "center" }}>
                  +{positionLegend.length - visiblePositionChips.length}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setPositionLegendCollapsed(!positionLegendCollapsed)}
              title={positionLegendCollapsed ? "Expand positions" : "Collapse positions"}
              style={{
                width: positionLegendCollapsed ? 20 : 22,
                height: positionLegendCollapsed ? 20 : 22,
                borderRadius: 4,
                border: "1px solid rgba(148, 163, 184, 0.28)",
                background: "rgba(15, 23, 42, 0.34)",
                color: "#e5e7eb",
                lineHeight: positionLegendCollapsed ? "16px" : "18px",
                cursor: "pointer",
                padding: 0
              }}
            >
              {positionLegendCollapsed ? "+" : "-"}
            </button>
          </div>
          {!positionLegendCollapsed && selectedPosition && (() => {
            const sideColor = selectedPosition.positionSide === "LONG" ? "#4ade80" : "#f87171";
            const pnl = Number(selectedPosition.lane?.unrealizedPnl);
            const pnlColor = !Number.isFinite(pnl) ? "#e5e7eb" : pnl >= 0 ? "#4ade80" : "#f87171";
            return (
              <div style={{ marginTop: 7, paddingTop: 7, borderTop: "1px solid rgba(148, 163, 184, 0.22)", maxHeight: 190, overflowY: "auto" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 5, minWidth: 0 }}>
                  <strong style={{ color: sideColor, fontSize: 13, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {selectedPosition.pending ? "PENDING " : ""}{selectedPosition.positionSide} {selectedPosition.symbol}
                  </strong>
                  {Number.isFinite(pnl) ? <strong style={{ color: pnlColor }}>{formatPnl(pnl)}</strong> : null}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "62px minmax(0, 1fr)", gap: "4px 8px" }}>
                  <span style={{ color: "#9ca3af" }}>Position</span>
                  <span style={{ overflowWrap: "anywhere" }}>{positionSummaryText(selectedPosition)}</span>
                  {selectedPosition.lane ? (
                    <>
                      <span style={{ color: "#9ca3af" }}>Entry</span>
                      <span>{formatTradePrice(selectedPosition.lane.entryPrice)}</span>
                      <span style={{ color: "#9ca3af" }}>Mark</span>
                      <span>{formatTradePrice(selectedPosition.lane.markPrice)}</span>
                    </>
                  ) : null}
                </div>
                {selectedPosition.orders.length > 0 ? (
                  <div style={{ marginTop: 8, paddingTop: 7, borderTop: "1px solid rgba(148, 163, 184, 0.18)" }}>
                    {selectedPosition.orders.map((order) => {
                      const label = positionOrderLabel(order);
                      const orderColor = label === "SL" ? "#fb923c" : label === "TP" ? "#7dd3fc" : label === "OPEN" ? sideColor : "#cbd5e1";
                      return (
                        <div key={order.orderId || `${label}-${order.createdAt}-${order.price}`} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "2px 0" }}>
                          <span style={{ color: orderColor }}>{label} {order.quantity != null ? `${order.quantity} qty` : ""}</span>
                          <strong>{positionOrderPrice(order)}</strong>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default TradingViewChart;

